import { getTableColumnsData } from '../../shared/tables/tableColumns';
import { applyTableConfigPresentation } from '../../shared/tables/tableConfigPresentation';

/**
 * Matérialisation d'un **tableau variable** du site : réponse brute de l'API +
 * `tableConfig` → les `columns` et `rows` du bloc.
 *
 * C'est le seul endroit qui traduit le vocabulaire du document (`title`,
 * `width` en px, `type`) vers celui du bloc web (`label`, `widthPercent`,
 * `cellType`). Tout le reste — quelles colonnes, dans quel ordre, sous quel
 * nom, de quelle largeur — est décidé par le moteur partagé avec le Proposal.
 *
 * Le point qui compte : le contenu est **entièrement recalculé**, jamais
 * rapproché de ce que le bloc portait déjà. L'ancien chemin relisait les
 * cellules par libellé de colonne ; comme le libellé s'éditait au canvas et
 * finissait par valoir `<p>Prestation</p>`, toute colonne dont on avait touché
 * le titre se vidait au rechargement. Ici la question ne se pose plus.
 *
 * Les largeurs sont calculées sur une box de 100 : le « px » du moteur est
 * donc directement le pourcentage attendu par le bloc.
 */

const NOMINAL_WIDTH = 100;

/** Clé des lignes d'une réponse `getTable` (`rows`, ou la première `rows_…` si split). */
function apiRowsKey(table) {
  if (!table || typeof table !== 'object') return null;
  if (Array.isArray(table.rows)) return 'rows';
  return Object.keys(table).find((k) => k.startsWith('rows') && Array.isArray(table[k])) ?? null;
}

/** Lignes d'une réponse `getTable` (`rows`, ou la première `rows_…` si split). */
export function apiRowsOf(table) {
  const key = apiRowsKey(table);
  return key ? table[key] : null;
}

function cellValue(row, title) {
  const raw = row?.[title];
  if (raw == null) return '';
  return typeof raw === 'string' ? raw : String(raw);
}

/**
 * Plage de lignes du bloc, comme `content.rowsSelectionStart/End` du Proposal
 * (`SelectTableRows`) : bornes comptées à partir de 1, incluses ; absente, la
 * table entière. Elle s'applique à chaque matérialisation, donc survit aux
 * relectures.
 */
function rowsInRange(rows, { rowsSelectionStart, rowsSelectionEnd } = {}) {
  return rows.slice((rowsSelectionStart || 1) - 1, rowsSelectionEnd || rows.length);
}

/**
 * Une slice de lignes brutes (`table[rowsKey]`) → `columns`, `rows`,
 * `columnsDisposition`. `row_kinds` est indexé par la même clé que la slice.
 */
function materializeRows(table, rowsKey, tableConfig, blockProps) {
  const content = applyTableConfigPresentation(
    getTableColumnsData(
      {
        columns_informations: table.columns_informations,
        rows: table[rowsKey],
        kinds: table.row_kinds?.[rowsKey],
      },
      NOMINAL_WIDTH,
    ),
    tableConfig,
    NOMINAL_WIDTH,
    blockProps.tableName,
  );

  const columns = content.columns.map((col) => ({
    key: col.key,
    label: col.title,
    // Libellé d'origine de la colonne, tel que l'API la nomme : c'est la clé de
    // matching des overrides, et c'est ce qui permet de reconnaître un
    // renommage fait au canvas.
    originalLabel: col.originalTitle || col.title,
    widthPercent: Number(col.width.toFixed(2)),
    cellType: col.type === 'image' ? 'image' : 'text',
    // L'API marque les colonnes dont les valeurs répétées se fusionnent
    // verticalement (ex. un profil qui couvre plusieurs lignes).
    merge: !!col.merge,
    textAlign: col.textAlign || '',
    verticalAlign: '',
  }));

  // `rowKind` : type de ligne donné par l'API (zone), lu par les règles de
  // fusion et de style. Gardé même nul : une ligne sans la clé a été lue avant
  // les types (cf. hasStaleRowKinds).
  const rows = rowsInRange(content.data, blockProps).map((row) => ({
    cells: content.columns.map((col) => ({ value: cellValue(row, col.title) })),
    ...('rowKind' in row ? { rowKind: row.rowKind } : {}),
  }));

  // Groupes d'en-têtes décrits par l'API (`columns_informations.children`) : le
  // moteur les construit déjà et les tient à jour des colonnes masquées, il ne
  // restait qu'à les transmettre au bloc.
  return { columns, rows, columnsDisposition: content.columnsDisposition || [] };
}

/**
 * Tableau découpé : une slice par entité (`rows_<id>`), comme les box que le
 * Proposal crée une par zone (`computeTableBoxContents`). Ici elles restent
 * dans **un seul** bloc — le web-editor n'insère jamais de bloc tout seul — et
 * suivent la donnée : une entité apparue ajoute sa slice, une entité disparue
 * retire la sienne.
 */
function materializeSlices(table, tableConfig, blockProps) {
  if (!table || typeof table !== 'object') return null;
  const splitSlices = Object.keys(table)
    .filter((key) => key.startsWith('rows') && Array.isArray(table[key]) && table[key].length)
    .map((key) => materializeRows(table, key, tableConfig, blockProps));
  return splitSlices.length ? { splitSlices } : null;
}

/**
 * @param {object} table - réponse brute de `getTable`
 * @param {object|null} tableConfig - axes présentation (colonnes) du bloc
 * @param {{ tableName?: string, rowsSelectionStart?: number|null,
 *           rowsSelectionEnd?: number|null, shouldSplit?: boolean }} [blockProps]
 *          - table (relecture de sa config), plage de lignes et découpe
 * @returns {{ columns: object[], rows: object[] } | { splitSlices: object[] } | null}
 *          null si illisible — l'appelant garde alors ce que le bloc portait déjà.
 */
export function materializeTable(table, tableConfig = null, blockProps = {}) {
  if (blockProps.shouldSplit) return materializeSlices(table, tableConfig, blockProps);
  const rowsKey = apiRowsKey(table);
  if (!rowsKey || !table[rowsKey].length) return null;
  return materializeRows(table, rowsKey, tableConfig, blockProps);
}

/**
 * Colonnes affichées par le bloc — celles de chaque slice quand il est
 * découpé. C'est là que se lisent les intitulés renommés au canvas.
 */
export function blockColumnsOf(props) {
  if (props?.shouldSplit) return (props.splitSlices || []).flatMap((slice) => slice.columns || []);
  return props?.columns;
}

/**
 * Le contenu matérialisé diffère-t-il de celui que le bloc porte ?
 *
 * Comparaison sur ce qui vient de la table — libellés, largeurs, types et
 * valeurs — et rien d'autre. L'ancienne comparaison sérialisait les lignes
 * entières : elle voyait une différence à chaque fois, ce qui déclenchait une
 * sauvegarde et un remontage complet de Puck à chaque « Rafraîchir les
 * variables », même quand la table n'avait pas bougé.
 */
export function materializedContentChanged(previous, next) {
  const columnsOf = (cols) => (cols || []).map(
    (c) => [c?.label, c?.widthPercent, c?.cellType, !!c?.merge],
  );
  // Un groupe d'en-tête se compare par ce qu'il affiche et ce qu'il couvre ;
  // absent ou vide, c'est la même chose.
  const groupsOf = (disposition) => (disposition || []).map(
    (entry) => [entry?.title ?? null, (entry?.children || []).map((child) => child?.key)],
  );
  const valuesOf = (rows) => (rows || []).map(
    (r) => [r?.rowKind ?? null, ...(r?.cells || []).map((c) => c?.value ?? '')],
  );
  const contentOf = (content) => [
    columnsOf(content?.columns), valuesOf(content?.rows), groupsOf(content?.columnsDisposition),
  ];
  if (next.splitSlices) {
    return JSON.stringify((previous?.splitSlices || []).map(contentOf))
      !== JSON.stringify(next.splitSlices.map(contentOf));
  }
  return JSON.stringify(contentOf(previous)) !== JSON.stringify(contentOf(next));
}

/** Texte nu d'un libellé de colonne : il s'édite au canvas, donc en HTML. */
function plainLabel(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/**
 * Reporte dans la configuration les intitulés de colonnes renommés **au
 * canvas**, comme le fait le Proposal (`setTableColumnCustomLabel`) : sans ça
 * le renommage serait perdu à la première relecture, puisque le contenu d'un
 * tableau branché est entièrement recalculé.
 *
 * Le rapprochement se fait sur le libellé d'origine, jamais sur celui qui est
 * affiché — c'est justement lui qui a changé.
 *
 * @returns {object|null} la même référence si rien n'a été renommé
 */
export function foldCanvasLabels(tableConfig, columns) {
  if (!Array.isArray(columns) || !columns.length) return tableConfig;

  const overrides = Array.isArray(tableConfig?.columns) ? [...tableConfig.columns] : [];
  const indexByLabel = new Map(overrides.map((o, i) => [o.label, i]));
  let changed = false;

  columns.forEach((column) => {
    const original = column?.originalLabel;
    if (!original) return;
    const shown = plainLabel(column.label);
    const idx = indexByLabel.get(original);
    const current = idx != null ? overrides[idx]?.customLabel : undefined;
    // Retour au nom d'origine : l'override disparaît, comme le bouton
    // « rétablir » de l'écran de configuration.
    const customLabel = shown && shown !== original ? shown : undefined;
    if (customLabel === current) return;

    changed = true;
    if (idx != null) {
      overrides[idx] = { ...overrides[idx], customLabel };
    } else {
      overrides.push({ label: original, customLabel });
    }
  });

  if (!changed) return tableConfig;
  return { ...(tableConfig || {}), columns: overrides };
}
